import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { DropdownMenuItem } from '../../../components/DropdownMenu';
import RowActionsMenu from '../../../components/RowActionsMenu';
import SearchBar from '../../../components/SearchBar';
import TablePager from '../../../components/TablePager';
import { makeCustomerAccountDetailUrl } from '../../../routes';
import { tenantSelector } from '../../../selector/authenticationSelector';
import {
    useGetAccountsQuery,
    useUpdateAccountStatusMutation,
} from '../../../api/customerAccount/customerAccountApiSlice';
import { AccountSummary } from '../../../api/customerAccount/customerAccountApiTypes';
import { formatDate } from '../../../utils';
import { cn } from '../../../utils/utils';
import { onRowClick } from '../../../utils/rowClick';

const headerCellClass = 'text-left px-4 py-2.5 text-foreground/60 font-medium';
const cellClass = 'px-4 py-2.5 text-foreground/80';
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

/**
 * Customer accounts — end-user accounts (H-3a). Read-only: accounts are created
 * and edited by the end user in the booking app, never by an operator, so there
 * is no create button and the row menu offers only "View details".
 *
 * Backed by `GET /accounts`, which filters `deleted_at: null`
 * (`customerAccountController.ts`). A GDPR-deleted account is therefore never
 * listed — its PII is blanked and its row is gone from here.
 *
 * PAGINATED, and it has to be. The endpoint applies `DEFAULT_PAGE_SIZE = 25`
 * whether or not the caller asks for a page, and this tab used to send no
 * arguments and render `items` while ignoring `total` — so a tenant with more
 * than 25 accounts silently saw the first 25 and nothing said so.
 */
const IndividualCustomersTab: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);

    // skip until the tenant is restored — see the hard-reload race note in
    // OrganizationsTab (same middleware-reset-vs-in-flight-query freeze).
    const tenant = useSelector(tenantSelector);

    // Reset paging when the tenant changes (CLAUDE.md: state tied to a tenant
    // resets on context switch). Guarded so first mount is a no-op.
    const previousTenantRef = useRef(tenant);
    useEffect(() => {
        if (previousTenantRef.current !== tenant) {
            previousTenantRef.current = tenant;
            setSearch('');
            setPage(0);
        }
    }, [tenant]);

    const [updateAccountStatus] = useUpdateAccountStatusMutation();
    const [statusError, setStatusError] = useState<string | null>(null);

    const { data, isLoading, isFetching, isError } = useGetAccountsQuery(
        {
            search: search.trim() || undefined,
            page: page + 1, // backend is 1-based; the pager is 0-based
            pageSize,
        },
        { skip: !tenant },
    );
    const items = data?.items ?? [];
    const total = data?.total ?? 0;

    /**
     * #2102 item 5. `accountStatus` is the RAW enum, not the display `status`:
     * that one collapses `active` + unverified into `unverified`, so deciding
     * lock-or-unlock from it would mean re-deriving a rule the server owns.
     *
     * A `deleted` account offers neither — the erasure flow has already blanked
     * its PII and login is refused regardless. The endpoint refuses it too, so
     * hiding the action here is convenience, not the enforcement.
     */
    const rowActions = (account: AccountSummary): DropdownMenuItem[] => {
        // "Details anzeigen" is not here: the row click opens the account and
        // the name is the keyboard's link (design-decisions.md ›
        // Zeilenaktionen in Tabellen). A deleted account has nothing left to
        // change, so its row gets no ⋮.
        const actions: DropdownMenuItem[] = [];
        if (account.accountStatus === 'deleted') return actions;

        const nextStatus = account.accountStatus === 'locked' ? 'active' : 'locked';
        actions.push({
            id: 'lock',
            label: nextStatus === 'locked'
                ? t('customers.individuals.actions.lock')
                : t('customers.individuals.actions.unlock'),
            onClick: async () => {
                setStatusError(null);
                try {
                    await updateAccountStatus({ id: account.id, status: nextStatus }).unwrap();
                } catch {
                    // Surfaced rather than swallowed: barring an abusive account
                    // is the point, and a silent failure looks like success.
                    setStatusError(t('customers.individuals.actions.error'));
                }
            },
        });
        return actions;
    };

    const naLabel = t('common.not_applicable');

    const fullNameOf = (account: AccountSummary): string => {
        const name = [account.firstName, account.lastName]
            .map((part) => part?.trim() ?? '')
            .filter((part) => part !== '')
            .join(' ');
        return name || naLabel;
    };

    // `account_status_enum` is active | locked | deleted. It used to be
    // translated as active | pending | disabled — two labels for states that do
    // not exist, and none for `locked`, the only non-active state that can
    // actually be listed. The raw-value fallback hid it: a locked account read
    // "locked" in both languages rather than looking broken.
    const statusLabelOf = (account: AccountSummary): string =>
        account.status
            ? t(`customers.individuals.status.${account.status.toLowerCase()}`, {
                defaultValue: account.status,
            })
            : naLabel;

    if (isLoading || !tenant) {
        return (
            <div className={'flex justify-center py-16'}>
                <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
            </div>
        );
    }

    if (isError) {
        return (
            <p className={'text-sm text-foreground/60'}>{t('customers.individuals.load_error')}</p>
        );
    }

    // Only a genuinely empty tenant gets the bare empty state. An empty PAGE
    // keeps the table and its pager, or the operator would land on a dead
    // screen with no control to get back to page one — and an empty SEARCH
    // result keeps the search box, or there would be no way to clear the term
    // that produced it (the same dead-end, one control further along).
    const isEmpty = total === 0;
    const emptyMessage = search.trim()
        ? t('customers.individuals.no_search_results')
        : t('customers.individuals.empty');

    const openDetail = (account: AccountSummary) =>
        navigate(makeCustomerAccountDetailUrl(account.id));

    return (
        <div className={'flex flex-col gap-6'}>
            {statusError && (
                <p className={'text-sm text-red-600'}>{statusError}</p>
            )}

            <SearchBar
                value={search}
                placeholder={t('customers.individuals.search_placeholder')}
                onChange={(value) => {
                    setSearch(value);
                    // Back to page one: the term describes a different result
                    // set, and staying on page 4 of the old one shows nothing.
                    setPage(0);
                }}
            />

            {isEmpty ? (
                <p className={'text-sm text-foreground/60'}>{emptyMessage}</p>
            ) : (
                <div className={'bg-text-contrast rounded-[5px] shadow-sm'}>
                    <div className={'overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/10'}>
                                    <th className={headerCellClass}>{t('customers.individuals.columns.name')}</th>
                                    <th className={headerCellClass}>{t('customers.individuals.columns.email')}</th>
                                    <th className={headerCellClass}>{t('customers.individuals.columns.phone')}</th>
                                    <th className={headerCellClass}>{t('customers.individuals.columns.created')}</th>
                                    <th className={headerCellClass}>{t('customers.individuals.columns.status')}</th>
                                    {/* Actions column: no header text, as in the
                                organizations and bookings tables. */}
                                    <th className={headerCellClass} />
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((account) => (
                                    <tr
                                        key={account.id}
                                        onClick={onRowClick(() => openDetail(account))}
                                        className={cn(
                                            'border-b border-foreground/5 last:border-b-0',
                                            'cursor-pointer hover:bg-foreground/5 transition-colors',
                                        )}
                                    >
                                        <td className={cellClass}>
                                            <Link
                                                to={makeCustomerAccountDetailUrl(account.id)}
                                                className={'text-primary hover:underline'}
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                {fullNameOf(account)}
                                            </Link>
                                        </td>
                                        <td className={cellClass}>{account.email}</td>
                                        <td className={cellClass}>{account.phone?.trim() || naLabel}</td>
                                        <td className={cellClass}>{formatDate(account.createdAt, language)}</td>
                                        <td className={cellClass}>{statusLabelOf(account)}</td>
                                        <td className={`${cellClass} text-right`}>
                                            <RowActionsMenu rowName={fullNameOf(account)} items={rowActions(account)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <TablePager
                        count={total}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        onPageChange={setPage}
                        onRowsPerPageChange={(value) => {
                            setPageSize(value);
                            setPage(0);
                        }}
                    />
                </div>
            )}

            {/* Refetching an existing list (a new search term, a page change)
                keeps the current rows on screen rather than flashing the
                spinner, so the table does not jump under the cursor. */}
            {isFetching && !isLoading && (
                <p className={'text-xs text-foreground/40'}>{t('common.loading')}</p>
            )}
        </div>
    );
};

export default IndividualCustomersTab;
